import React, {Fragment, useEffect, useRef, useState} from "react";
import Nestable from "react-nestable";
import {DateInput, NumberInput, RadioInput, TextArea, TextFieldInput,} from "./elements";
import Header from "./Header";
import {
    addAnswerRequest,
    addChildQuestionRequest,
    addQuestionRequest,
    deleteAnswerRequest,
    deleteChildQuestionRequest,
    deleteQuestionRequest,
    fetchQuestionsRequest,
    resetQuestions,
    updateAnswerRequest, updateChildAnswerRequest,
    updateChildQuestionRequest,
    updateQuestionRequest
} from "../../slices/questionSlice";
import {resetExam} from "../../slices/examSlice";
import {Option, Question, QuestionTypes, SubQuestion} from "../../types/Question";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../store/store";
import {useParams} from "react-router-dom";
import {formatQuestionData} from "../../utils/formatQuestion";
import QuestionGroup from "./elements/QuestionGroup";
import SpeakInput from "./elements/SpeakInput";
import DragAndDropInput from "./elements/DragAndDropInput";
import CheckboxInput from "./elements/CheckboxInput";
import {PlusCircleIcon} from "lucide-react";
import uuid from "react-uuid";
import {apiRequestWithAuth} from "../../utils/api";
import Listen from "./elements/Listen";
import 'react-nestable/dist/styles/index.css';


const FormBuilder: React.FC = () => {
    const { examId } = useParams<{ examId: string }>();

    const initVal = QuestionTypes.QUESTION_TYPE_SHORT_ANSWER;
    const dispatch = useDispatch();

    const [title, setTitle] = useState("Untitled Form");
    const [description, setDescription] = useState("");
    const questions = useSelector((state: RootState) => state.questions.questions);
    const exam = useSelector((state: RootState) => state.exam.exam || { id: "", title: "", shortDescription: "" });
    const [examData, setExamData] = useState<any>({ id: "", title: "", shortDescription: "" });
    const [dataQuestion, setDataQuestion] = useState<Question[]>([]);
    const [formData, setFormData] = useState<QuestionTypes>(QuestionTypes.QUESTION_TYPE_SHORT_ANSWER);
    const hasFetched = useRef(false);

    useEffect(() => {
        dispatch(resetQuestions());
        dispatch(resetExam());
        if (hasFetched.current) return;
        hasFetched.current = true;
        dispatch(fetchQuestionsRequest({examId}));
    }, [examId]);

    useEffect(() => {
        const mergedQuestion = mergerQuestion(dataQuestion, questions);

        const updatedQuestions = mergedQuestion.map(question => {
            if (question.displayIds && question.answers) {
                const displayIds = question.displayIds.split(',');
                const displayAnswers = displayIds.map(id =>
                    question.answers?.find(answer => answer.id == id)
                ).filter(Boolean) as Option[];

                return { ...question, displayAnswers };
            }

            // Xử lý displayAnswers cho child questions
            if (question.childs) {
                const updatedChilds = question.childs.map(child => {
                    if (child.displayIds && child.answers) {
                        const displayIds = child.displayIds.split(',');
                        const displayAnswers = displayIds.map(id =>
                            child.answers?.find(answer => answer.id == id)
                        ).filter(Boolean) as Option[];

                        return { ...child, displayAnswers };
                    }
                    return child;
                });

                return { ...question, childs: updatedChilds };
            }

            return question;
        });

        setDataQuestion(updatedQuestions)
        setExamData(exam);
    }, [questions]);

    if (!examId) {
        return <div style={{ textAlign: "center", color: "red", fontSize: "18px" }}>Không tìm thấy bài thi</div>;
    }

    const mergerQuestion = (oldQs: Question[], newQs: Question[]) => {
        const mergedData = oldQs.map((oldQ) => {
            const newQ = newQs.find((q) => q.uid === oldQ.uid);
            if (!newQ) return oldQ;

            // === Merge answers của chính parent ===
            const mergedAnswers = (oldQ.answers || []).map((oldAns: any) => {
                const newAns = (newQ.answers || []).find((a: any) => a.uid === oldAns.uid);
                return newAns ? { ...oldAns, ...newAns } : oldAns;
            });

            const addedAnswers = (newQ.answers || []).filter(
                (a: any) => !(oldQ.answers || []).some((oa: any) => oa.uid === a.uid)
            );

            // Merge childs
            const mergedChilds = (oldQ.childs || []).map((oldChild) => {
                const newChild = (newQ.childs || []).find((c) => c.uid === oldChild.uid);
                if (!newChild) return oldChild;

                // Merge answers
                const mergedAnswers = (oldChild.answers || []).map((oldAns) => {
                    const newAns = (newChild.answers || []).find((a) => a.uid === oldAns.uid);
                    return newAns ? { ...oldAns, ...newAns } : oldAns;
                });

                const addedAnswers = (newChild.answers || []).filter(
                    (a) => !(oldChild.answers || []).some((oa) => oa.uid === a.uid)
                );

                return {
                    ...oldChild,
                    ...newChild,
                    answers: [...mergedAnswers, ...addedAnswers],
                };
            });

            const addedChilds = (newQ.childs || []).filter(
                (c) => !(oldQ.childs || []).some((oc) => oc.uid === c.uid)
            );

            return {
                ...oldQ,
                ...newQ,
                answers: [...mergedAnswers, ...addedAnswers],
                childs: [...mergedChilds, ...addedChilds],
            };
        });

        // Add new top-level questions if not in old list
        const addedQuestions = newQs.filter(
            (q) => !oldQs.some((d) => d.uid === q.uid)
        );

        return [...mergedData, ...addedQuestions];
    }

    const addElement = async () => {
        const defaultOptions = [
            { uid: crypto.randomUUID(), title: "", isCorrect: 0},
        ];

        const newElement: Question = {
            uid: crypto.randomUUID(), // Tạo ID ngẫu nhiên cho phần tử mới
            title: "Untitled",
            value: "",
            blanks: [],
            correctAnswer: null,
            inputs: [],
            type: formData,
            required: false,
            answers: formData === QuestionTypes.QUESTION_TYPE_SINGLE || formData === QuestionTypes.QUESTION_TYPE_MULTI || formData === QuestionTypes.QUESTION_TYPE_SELECT_OPTION ? defaultOptions : undefined,
            answerText: formData === QuestionTypes.QUESTION_TYPE_SHORT_ANSWER || formData === QuestionTypes.QUESTION_TYPE_LONG_ANSWER ? '' : undefined,
            childs: formData === QuestionTypes.QUESTION_TYPE_GROUP ? [] : undefined // Nhóm câu hỏi có childs
        };

        const data = formatQuestionData(newElement);

        dispatch(addQuestionRequest({ examId, data }));
        setFormData(initVal);
    };

    const deleteEl = (uuid: {uid: string, id?: string }) => {
        const {id, uid} = uuid;
        if (id) {
            dispatch(deleteQuestionRequest({id, examId}));
        }
        setDataQuestion((prevState) => prevState.filter((val) => val.uid !== uid));
    };

    const deleteElChildQuestion = (uuid: {uid: string, id?: string }) => {
        const {id, uid} = uuid;
        if (id) {
            dispatch(deleteChildQuestionRequest({id, examId, uid}));
        }
        // Xóa phần tử con khỏi danh sách câu hỏi
        setDataQuestion((prevState) => {
            return prevState.map((el) => {
                if (el.childs) {
                    return {
                        ...el,
                        childs: el.childs.filter((child) => child.uid !== uid)
                    };
                }
                return el;
            });
        });
    };

    const addAfter = (elArray: Question[], index: number, newEl: Question): Question[] => {
        return [...elArray.slice(0, index + 1), newEl, ...elArray.slice(index + 1)];
    };

    const addAfterChild = (elArray: Question[], index: number, newEl: SubQuestion): Question[] => {
        return elArray.map((el, idx) => {
            if (idx === index && el.childs) {
                return { ...el, childs: [...el.childs, newEl] };
            }
            return el;
        });
    };

    const duplicateElement = (elId: string, type: QuestionTypes) => {
        let elIdx = dataQuestion.findIndex((el) => el.id === elId);
        if (elIdx === -1) return;

        let originalEl = dataQuestion[elIdx];

        let newEl: Question = {
            ...originalEl, // Sao chép toàn bộ thuộc tính từ phần tử gốc
            uid: crypto.randomUUID(), // Tạo ID mới
            title: originalEl.title ?? null, // Đảm bảo giữ giá trị hợp lệ
            value: originalEl.value ?? null, // Đảm bảo giữ giá trị hợp lệ
            answers: originalEl.answers ? originalEl.answers.map(opt => ({ ...opt, uid: crypto.randomUUID() })) : undefined, // Sao chép và tạo ID mới cho answers
            childs: originalEl.childs ? originalEl.childs.map(subQ => ({ ...subQ, uid: crypto.randomUUID() })) : undefined // Sao chép childs nếu có
        };

        const data = formatQuestionData(newEl);
        dispatch(addQuestionRequest({ examId, data }));

        let newArr = addAfter(dataQuestion, elIdx, newEl);
        setDataQuestion(newArr);
    };

    const duplicateElementChildQuestion = (elId: string, type: QuestionTypes) => {
        // Tìm phần tử con cần sao chép
        let elIdx = dataQuestion.findIndex((el) => el.childs && el.childs.some(child => child.id === elId));
        if (elIdx === -1) return;
        let originalEl = dataQuestion[elIdx]?.childs?.find(child => child.id === elId);

        const newEl: Question = {
            ...originalEl,
            uid: crypto.randomUUID(),
            examId: examId,
            title: originalEl?.title || 'Untitled',
            value: originalEl?.value || '',
            type: originalEl?.type || QuestionTypes.QUESTION_TYPE_SHORT_ANSWER,
            required: originalEl?.required || false,
            answers: originalEl?.answers?.map(opt => ({
                ...opt,
                uid: crypto.randomUUID(),
            })),
        };

        let newArr = addAfterChild(dataQuestion, elIdx, newEl);
        const data = formatQuestionData(newEl);
        dispatch(addChildQuestionRequest(data));

        setDataQuestion(newArr);
    };

    const handleOnChangeSort = (data: { items: any }) => {
        setDataQuestion(data.items);
        const ids = data.items
            .filter((item: Question) => item.id)
            .map((item: Question) => item.id);

        if (ids.length > 0) {
            try {
                apiRequestWithAuth(`/quiz/${examId}/orderNo`, 'PUT', {
                    ids: ids
                });
            } catch (error) {
                console.error('Lỗi cập nhật thứ tự câu hỏi:', error);
            }
        }
    };

    const handleOnChangeSortChild = async (parentId: string, data: { items: any }) => {
        setDataQuestion(prevData =>
            prevData.map(el => {
                if (el.id === parentId) {
                    return { ...el, childs: data.items };
                }
                return el;
            })
        );

        const ids = data.items
            .filter((item: SubQuestion) => item.id)
            .map((item: SubQuestion) => item.id);

        if (ids.length > 0) {
            try {
                await apiRequestWithAuth(`/quiz/${examId}/orderNo`, 'PUT', {
                    ids: ids
                });
            } catch (error) {
                console.error('Lỗi cập nhật thứ tự câu hỏi con:', error);
            }
        }
    };

    const handleValue = (id: string, newValue: string, type: QuestionTypes) => {
        const newData = dataQuestion.map((el) => {
            if (el.id === id) {
                let updatedEl = { ...el };

                switch (type) {
                    case QuestionTypes.QUESTION_TYPE_SINGLE:
                    case QuestionTypes.QUESTION_TYPE_MULTI:
                    case QuestionTypes.QUESTION_TYPE_SELECT_OPTION:
                        updatedEl.title = newValue;
                        break;

                    case QuestionTypes.QUESTION_TYPE_SHORT_ANSWER:
                    case QuestionTypes.QUESTION_TYPE_LONG_ANSWER:
                        updatedEl.title = newValue; // Cập nhật câu trả lời dạng văn bản
                        break;

                    case QuestionTypes.QUESTION_TYPE_DRAG_AND_DROP:
                        updatedEl.title = newValue;
                        break;

                    case QuestionTypes.QUESTION_TYPE_SPEAK:
                        updatedEl.title = newValue; // Cập nhật link ghi âm
                        break;

                    case QuestionTypes.QUESTION_TYPE_GROUP:
                        updatedEl.title = newValue;
                        if (Array.isArray(updatedEl.childs)) {
                            // const subQuestionIndex = Number(e.target.dataset.index);
                            // updatedEl.childs = updatedEl.childs.map((subQ, index) =>
                            //     index === subQuestionIndex
                            //         ? { ...subQ, value: newValue }
                            //         : subQ
                            // );
                        }
                        break;

                    default:
                        updatedEl.value = newValue;
                        break;
                }

                const formattedData= formatQuestionData(updatedEl);

                dispatch(updateQuestionRequest({ id, examId, data: formattedData }));

                return updatedEl;
            }
            return el;
        })
        const mergedQuestion = mergerQuestion(dataQuestion, newData);
        setDataQuestion(mergedQuestion);
    };

    const handleValueChildQuestion = (id: string, newValue: string, type: QuestionTypes) => {
        const newData = dataQuestion.map((el) => {
            if (el.childs) {
                const updatedChilds = el.childs.map((child) => {
                    if (child.id === id) {
                        let updatedChildEl = { ...child };

                        switch (type) {
                            case QuestionTypes.QUESTION_TYPE_SINGLE:
                            case QuestionTypes.QUESTION_TYPE_MULTI:
                            case QuestionTypes.QUESTION_TYPE_SELECT_OPTION:
                                updatedChildEl.title = newValue;
                                break;

                            case QuestionTypes.QUESTION_TYPE_SHORT_ANSWER:
                            case QuestionTypes.QUESTION_TYPE_LONG_ANSWER:
                                updatedChildEl.title = newValue; // Cập nhật câu trả lời dạng văn bản
                                break;

                            case QuestionTypes.QUESTION_TYPE_DRAG_AND_DROP:
                                if (Array.isArray(updatedChildEl.blanks)) {
                                    // const blankIndex = Number(e.target.dataset.index);
                                    // updatedEl.blanks = updatedEl.blanks.map((blank, index) =>
                                    //     index === blankIndex
                                    //         ? { ...blank, answer: newValue }
                                    //         : blank
                                    // );
                                }
                                break;

                            case QuestionTypes.QUESTION_TYPE_SPEAK:
                                updatedChildEl.title = newValue; // Cập nhật link ghi âm
                                break;

                            case QuestionTypes.QUESTION_TYPE_GROUP:
                                if (Array.isArray(updatedChildEl.childs)) {
                                    // const subQuestionIndex = Number(e.target.dataset.index);
                                    // updatedEl.childs = updatedEl.childs.map((subQ, index) =>
                                    //     index === subQuestionIndex
                                    //         ? { ...subQ, value: newValue }
                                    //         : subQ
                                    // );
                                }
                                break;

                            default:
                                updatedChildEl.value = newValue;
                                break;
                        }

                        const formattedData= formatQuestionData(updatedChildEl);

                        dispatch(updateChildQuestionRequest({ id, examId, data: formattedData }));

                        return updatedChildEl;
                    }
                    return child;
                });
                return { ...el, childs: updatedChilds };
            }
            return el;
        })
        const mergedQuestion = mergerQuestion(dataQuestion, newData);
        setDataQuestion(mergedQuestion);
    };

    const handleAnswerValue = (id: string, newValue: any, type: QuestionTypes) => {
        const newData = dataQuestion.map((el) => {
            if (el.id === id) {
                let updatedEl = { ...el };

                updatedEl.answerText = newValue;

                const formattedData= formatQuestionData(updatedEl);

                dispatch(updateQuestionRequest({ id, examId, data: formattedData }));

                return updatedEl;
            }
            return el;
        });
        const mergedQuestion = mergerQuestion(dataQuestion, newData);
        setDataQuestion(mergedQuestion);
    };

    const handleAnswerValueChildQuestion = (id: string, newValue: any, type: QuestionTypes) => {
        const newData = dataQuestion.map((el) => {
            if (el.childs) {
                const updatedChilds = el.childs.map((child) => {
                    if (child.id === id) {
                        let updatedChildEl = { ...child };

                        updatedChildEl.answerText = newValue;

                        const formattedData= formatQuestionData(updatedChildEl);

                        dispatch(updateChildQuestionRequest({ id, examId, data: formattedData }));

                        return updatedChildEl;
                    }
                    return child;
                });
                return { ...el, childs: updatedChilds };
            }
            return el;
        });
        const mergedQuestion = mergerQuestion(dataQuestion, newData);
        setDataQuestion(mergedQuestion);
    };

    const handleRequired = (id: string) => {
        setDataQuestion((prevData) =>
            prevData.map((el) => (el.id === id ? { ...el, required: !el.required } : el))
        );
    };

    const handleElType = (id: string, value: QuestionTypes) => {
        setDataQuestion((prevData) =>
            prevData.map((el) => {
                let updatedEl = { ...el };

                if (el.id === id) {
                    updatedEl.type = value;
                    // updatedEl.answers = shouldHaveOptions(value) ? generateDefaultOptions() : [];
                    updatedEl.answers = [];
                    const formattedData= formatQuestionData(updatedEl);
                    dispatch(updateQuestionRequest({ id, examId, data: formattedData }));
                }

                return updatedEl;
            })
        );
    };

    const handleElTypeChildQuestion = (id: string, value: QuestionTypes) => {
        setDataQuestion((prevData) =>
            prevData.map((el) => {
                let updatedEl = { ...el };

                if (el.childs) {
                    // Nếu là câu hỏi con
                    updatedEl.childs = el.childs.map((child) => {
                        if (child.id === id) {
                            const updatedChild = {
                                ...child,
                                type: value,
                                // answers: shouldHaveOptions(value) ? generateDefaultOptions() : [],
                                answers: [],
                            };
                            const formattedData = formatQuestionData(updatedChild);
                            dispatch(updateChildQuestionRequest({id, examId, data: formattedData}));
                            return updatedChild;
                        }
                        return child;
                    });
                }

                return updatedEl;
            })
        );
    };

    // Hàm kiểm tra loại câu hỏi có cần answers không
    const shouldHaveOptions = (type: any): boolean => {
        return [
            QuestionTypes.QUESTION_TYPE_SINGLE,
            QuestionTypes.QUESTION_TYPE_MULTI,
            QuestionTypes.QUESTION_TYPE_SELECT_OPTION
        ].includes(type);
    };

    // Hàm tạo danh sách lựa chọn mặc định
    const generateDefaultOptions = () => [
        { uid: crypto.randomUUID(), title: "", isCorrect: 0 },
    ];

    const addOption = (id: string, newOption: Option) => {
        setDataQuestion((prevData) =>
            prevData.map((el) => {
                if (el.id === id) {
                    return { ...el, answers: [...(el.answers || []), newOption] };
                }
                return el;
            })
        );
    };

    const addOptionChildQuestion = (id: string, newOption: Option) => {
        setDataQuestion((prevData) =>
            prevData.map((el) => {
                if (el.childs) {
                    const updatedChilds = el.childs.map((child) => {
                        if (child.id === id) {
                            return { ...child, answers: [...(child.answers || []), newOption] };
                        }
                        return child;
                    });
                    return { ...el, childs: updatedChilds };
                }
                return el;
            })
        );
    };

    const handleOptionValues = (elId: string, answer: {optionId: string, uid: string, optionVal: string, isCorrect: number}, type: QuestionTypes) => {
        const optionId = answer.optionId;
        const optionUId = answer.uid;
        const optionVal = answer.optionVal;
        setDataQuestion((prevData) =>
            prevData.map((el) => {
                if (el.id === elId && el.answers) {
                    if (type !== QuestionTypes.QUESTION_TYPE_MULTI) {
                        el = {
                            ...el,
                            answers: el.answers?.map(option => ({
                                ...option,
                                isCorrect: 0,
                            }))
                        };
                    }

                    el.answers?.map((opt) => {
                        if (opt.uid === optionUId) {
                            if (!isNaN(Number(optionId)) && Number(optionId) > 0) {
                                dispatch(updateAnswerRequest({
                                    examId: examId,
                                    questionId: elId,
                                    answerId: optionId,
                                    title: optionVal,
                                    type: type,
                                    isCorrect: answer.isCorrect
                                }));
                            } else {
                                dispatch(addAnswerRequest({
                                    examId: examId,
                                    uid: answer.uid || uuid(),
                                    questionId: elId,
                                    title: optionVal,
                                    type: type,
                                    isCorrect: answer.isCorrect
                                }));
                            }
                        }
                    })
                }
                return el;
            })
        );
    };

    const handleOptionValuesChildQuestion = (elId: string, answer: {optionId: string, uid: string, optionVal: string, isCorrect: number}, type: QuestionTypes) => {
        const optionId = answer.optionId;
        const optionUId = answer.uid;
        const optionVal = answer.optionVal;
        setDataQuestion((prevData) =>
            prevData.map((el) => {
                if (el.childs) {
                    const updatedChilds = el.childs.map((child) => {
                        if (child.id === elId && child.answers) {
                            let newAnswers = child.answers;
                            // Nếu không phải multi thì reset isCorrect tất cả về 0
                            if (type !== QuestionTypes.QUESTION_TYPE_MULTI) {
                                newAnswers = newAnswers.map(option => ({
                                    ...option,
                                    isCorrect: 0
                                }));
                            }
                            // Update lại 1 answer đúng
                            newAnswers = newAnswers.map((opt) => {
                                if (opt.uid === optionUId) {
                                    const newOpt = {
                                        ...opt,
                                        title: optionVal,
                                        isCorrect: answer.isCorrect
                                    };
                                    if (!isNaN(Number(optionId)) && Number(optionId) > 0) {
                                        dispatch(updateChildAnswerRequest({
                                            examId: examId,
                                            questionId: elId,
                                            answerId: optionId,
                                            title: optionVal,
                                            type: type,
                                            isCorrect: answer.isCorrect
                                        }));
                                    } else {
                                        dispatch(addAnswerRequest({
                                            examId: examId,
                                            uid: answer.uid || uuid(),
                                            questionId: elId,
                                            title: optionVal,
                                            type: type,
                                            isCorrect: answer.isCorrect
                                        }));
                                    }
                                    return newOpt;
                                }
                                return opt;
                            });
                            return { ...child, answers: newAnswers };
                        }
                        return child;
                    });
                    return { ...el, childs: updatedChilds };
                }
                return el;
            })
        );

    };

    const deleteOption = async (elId: string, uid: string, optionId: string) => {
        const formatData = dataQuestion.map((el) => {
            if (el.id === elId && el.answers) {
                const updatedEl = {
                    ...el,
                    answers: el.answers.filter((opt) => opt.uid !== uid),
                };

                if (optionId) {
                    dispatch(deleteAnswerRequest({ examId: examId, questionId: elId, answer: {id: optionId} }));
                }

                return updatedEl;
            }
            return el;
        });
        setDataQuestion(formatData);
    };

    const deleteOptionChildQuestion = async (elId: string, uid: string, optionId: string) => {
        const formatData = dataQuestion.map((el) => {
            if (el.childs) {
                const updatedChilds = el.childs.map((child) => {
                    if (child.id === elId && child.answers) {
                        const updatedEl = {
                            ...child,
                            answers: child.answers.filter((opt) => opt.uid !== uid),
                        };

                        dispatch(deleteAnswerRequest({ examId: examId, questionId: elId, answer: {id: optionId} }));
                        return updatedEl;
                    }
                    return child;
                });
                return { ...el, childs: updatedChilds };
            } else {
                return el;
            }
        });
        setDataQuestion(formatData);
    };

    const handleDate = (id: string, dateVal: Date | null) => {
        setDataQuestion((prevData) =>
            prevData.map((el) => (el.id === id ? { ...el, date: dateVal } : el))
        );
    };

    const handleTime = (id: string, dateVal: Date | null) => {
        setDataQuestion((prevData) =>
            prevData.map((el) => (el.id === id ? { ...el, time: dateVal } : el))
        );
    };

    const uploadFileListen = async (questionId: string, file: File) => {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        formDataUpload.append("subject", "question");
        formDataUpload.append("id", questionId);

        try {
            const res = await apiRequestWithAuth("/media/file", "POST", formDataUpload);
            // const newData = dataQuestion.map((el) => {
            //     if (el.id === questionId) {
            //         return {
            //             ...el,
            //             file: res.result
            //         };
            //     }
            //     return el;
            // })
            // setDataQuestion(newData);

            return res.result?.id || null;
        } catch (error) {
            return null;
        }
    };

    const handleFileChange = async (questionId: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const uploadedId = await uploadFileListen(questionId, file);
        }
    };

    const handleAnswerValueListen = async (id: string, e: any, type: QuestionTypes) => {
        const file = e.target.files?.[0];
        if (file) {
            const uploadedId = await uploadFileListen(id, file);
        }
    };

    const handleUpdateAnswersOrder = (itemId: string, newAnswers: Option[]) => {
        setDataQuestion(prev =>
            prev.map(el =>
                el.id === itemId
                    ? { ...el, answers: newAnswers }
                    : el
            )
        );
    };

    const handleUpdateChildAnswersOrder = (itemId: string, newAnswers: Option[]) => {
        setDataQuestion(prev =>
            prev.map(el => {
                if (el.childs) {
                    const updatedChilds = el.childs.map(child =>
                        child.id === itemId
                            ? { ...child, answers: newAnswers }
                            : child
                    );
                    return { ...el, childs: updatedChilds };
                }
                return el;
            })
        );
    };

    const handleUpdateViewOrder = (itemId: string, displayAnswers: Option[]) => {
        const displayIds = displayAnswers.map(option => option.id).join(',');

        setDataQuestion(prev =>
            prev.map(el =>
                el.id === itemId
                    ? { ...el, displayAnswers, displayIds }
                    : el
            )
        );

        const updatedQuestion = dataQuestion.find(el => el.id === itemId);
        if (updatedQuestion) {
            const formattedData = formatQuestionData({
                ...updatedQuestion,
                displayIds
            });

            dispatch(updateQuestionRequest({ id: itemId, examId, data: formattedData }));
        }
    };

    const handleUpdateChildViewOrder = (itemId: string, displayAnswers: Option[]) => {
        const displayIds = displayAnswers.map(option => option.id).join(',');

        setDataQuestion(prev =>
            prev.map(el => {
                if (el.childs) {
                    const updatedChilds = el.childs.map(child =>
                        child.id === itemId
                            ? { ...child, displayAnswers, displayIds }
                            : child
                    );
                    return { ...el, childs: updatedChilds };
                }
                return el;
            })
        );
    };

    const renderElements = ({ item } : any) => {
        return (
            <div className="draggable-item" key={item.id}>
                {(() => {
                    switch (item.type) {
                        case QuestionTypes.QUESTION_TYPE_GROUP:
                            return <QuestionGroup item={item} handleValue={handleValue} handleOnChangeSort={handleOnChangeSortChild}
                                                  deleteEl={deleteEl} handleRequired={handleRequired} handleElType={handleElType} handleElTypeChildQuestion={handleElTypeChildQuestion}
                                                  duplicateElement={duplicateElement} addSubQuestion={addSubQuestion}
                                                  functions={{
                                                        handleAnswerValueChildQuestion,
                                                        handleDate,
                                                        handleTime,
                                                        addOptionChildQuestion,
                                                        handleOptionValuesChildQuestion,
                                                        deleteElChildQuestion,
                                                        deleteOptionChildQuestion,
                                                        duplicateElementChildQuestion,
                                                        handleValueChildQuestion,
                                                        handleUpdateChildAnswersOrder,
                                                        handleUpdateChildViewOrder
                                                  }} />;
                        case QuestionTypes.QUESTION_TYPE_SHORT_ANSWER:
                            return <TextFieldInput item={item} handleValue={handleValue} handleAnswerValue={handleAnswerValue} deleteEl={deleteEl} handleRequired={handleRequired} handleElType={handleElType} duplicateElement={duplicateElement} />;
                        case QuestionTypes.QUESTION_TYPE_LONG_ANSWER:
                            return <TextArea item={item} handleValue={handleValue} handleAnswerValue={handleAnswerValue} deleteEl={deleteEl} handleRequired={handleRequired} handleElType={handleElType} duplicateElement={duplicateElement} />;
                        case "number":
                            return <NumberInput item={item} handleValue={handleValue} deleteEl={deleteEl} handleRequired={handleRequired} handleElType={handleElType} duplicateElement={duplicateElement} />;
                        case QuestionTypes.QUESTION_TYPE_SELECT_OPTION:
                            return <RadioInput currentType={QuestionTypes.QUESTION_TYPE_SELECT_OPTION} item={item} handleValue={handleValue} deleteEl={deleteEl} handleRequired={handleRequired} handleElType={handleElType} addOption={addOption} handleOptionValues={handleOptionValues} deleteOption={deleteOption} duplicateElement={duplicateElement} />;
                        case QuestionTypes.QUESTION_TYPE_SINGLE:
                            return <RadioInput currentType={QuestionTypes.QUESTION_TYPE_SINGLE} item={item} handleValue={handleValue} deleteEl={deleteEl} handleRequired={handleRequired} handleElType={handleElType} addOption={addOption} handleOptionValues={handleOptionValues} deleteOption={deleteOption} duplicateElement={duplicateElement} />;
                        case QuestionTypes.QUESTION_TYPE_SPEAK:
                            return <SpeakInput item={item} handleValue={handleValue} deleteEl={deleteEl} handleRequired={handleRequired} handleElType={handleElType} duplicateElement={duplicateElement} />;
                        case QuestionTypes.QUESTION_TYPE_DRAG_AND_DROP:
                            return <DragAndDropInput updateViewOrder={handleUpdateViewOrder} updateAnswersOrder={handleUpdateAnswersOrder} currentType={QuestionTypes.QUESTION_TYPE_DRAG_AND_DROP} item={item} handleValue={handleValue} deleteEl={deleteEl} handleRequired={handleRequired} handleElType={handleElType} addOption={addOption} handleOptionValues={handleOptionValues} deleteOption={deleteOption} duplicateElement={duplicateElement} />;
                        case QuestionTypes.QUESTION_TYPE_MULTI:
                            return <CheckboxInput currentType={QuestionTypes.QUESTION_TYPE_MULTI} item={item} handleValue={handleValue} deleteEl={deleteEl} handleRequired={handleRequired} handleElType={handleElType} addOption={addOption} handleOptionValues={handleOptionValues} deleteOption={deleteOption} duplicateElement={duplicateElement} />;
                        case QuestionTypes.QUESTION_TYPE_LISTEN:
                            return <Listen item={item} handleValue={handleValue} handleOnChangeSort={handleOnChangeSortChild}
                                                  deleteEl={deleteEl} handleRequired={handleRequired} handleElType={handleElType} handleElTypeChildQuestion={handleElTypeChildQuestion}
                                                  duplicateElement={duplicateElement} addSubQuestion={addSubQuestion} handleAnswerValue={handleAnswerValueListen}
                                                  functions={{
                                                      handleAnswerValueChildQuestion,
                                                      handleDate,
                                                      handleTime,
                                                      addOptionChildQuestion,
                                                      handleOptionValuesChildQuestion,
                                                      deleteElChildQuestion,
                                                      deleteOptionChildQuestion,
                                                      duplicateElementChildQuestion,
                                                      handleValueChildQuestion,
                                                      handleUpdateChildAnswersOrder,
                                                      handleUpdateChildViewOrder
                                                  }} />;
                        case "date":
                            return <DateInput item={item} handleValue={handleValue} deleteEl={deleteEl} handleRequired={handleRequired} handleElType={handleElType} handleDate={handleDate} duplicateElement={duplicateElement} />;
                        case "time":
                            // return <TimeInput item={item} handleValue={handleValue} deleteEl={deleteEl} handleRequired={handleRequired} handleElType={handleElType} handleTime={handleTime} duplicateElement={duplicateElement} />;
                        default:
                            return <Fragment></Fragment>;
                    }
                })()}
            </div>
        );
    };

    const addSubQuestion = (parentId: string) => {
        const defaultOptions = [
            { uid: crypto.randomUUID(), title: "", isCorrect: 0 },
        ];

        const newSubQuestion: SubQuestion = {
            uid: crypto.randomUUID(),
            examId: examId,
            parentId: parentId,
            title: "Untitled",
            value: "",
            displayIds: "",
            blanks: [],
            correctAnswer: null,
            inputs: [],
            type: formData, // đây formData phải là kiểu QuestionTypes
            required: false,
            answers:
                formData === QuestionTypes.QUESTION_TYPE_SINGLE ||
                formData === QuestionTypes.QUESTION_TYPE_MULTI ||
                formData === QuestionTypes.QUESTION_TYPE_SELECT_OPTION
                    ? defaultOptions
                    : [],
            answerText:
                formData === QuestionTypes.QUESTION_TYPE_SHORT_ANSWER ||
                formData === QuestionTypes.QUESTION_TYPE_LONG_ANSWER
                    ? ""
                    : undefined,
        };

        setDataQuestion((prevData) =>
            prevData.map((el) =>
                el.id === parentId
                    ? { ...el, childs: [...(el.childs || []), newSubQuestion] }
                    : el
            )
        );

        const data = formatQuestionData(newSubQuestion);

        dispatch(addChildQuestionRequest(data));
    };

    return (
        <Fragment>
            <div className="container mx-auto max-w-4xl">
                <div className="flex flex-col items-center gap-6 container">
                    <div className="w-full">
                        <Header exam={examData} />
                        <Nestable
                            items={dataQuestion}
                            renderItem={renderElements}
                            maxDepth={1}
                            onChange={handleOnChangeSort}
                        />
                    </div>

                    <div className="sticky top-8">
                        <button
                            onClick={addElement}
                            className="flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white rounded-full p-3 shadow-md transition"
                            title="Thêm phần tử"
                        >
                            <PlusCircleIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default FormBuilder;
